
import { captureException as captureException_1, isEnabled as isEnabled_1 } from "./Shared/ErrorReporting.js";
import { ErrorBoundary, setUser as setUser_1, addBreadcrumb as addBreadcrumb_1, captureMessage as captureMessage_1, init as init_1 } from "@sentry/react";
import { fold } from "./fable_modules/fable-library-js.5.3.0/Seq.js";
import { printf, toText } from "./fable_modules/fable-library-js.5.3.0/String.js";
import { defaultOf } from "./fable_modules/fable-library-js.5.3.0/Util.js";
import { Fragment, createElement } from "react";
import { defaultOf as defaultOf_1 } from "./fable_modules/fable-library-js.5.3.0/Util.js";

export const Config_dsn = window.SENTRY_DSN || '';

/**
 * True when a DSN was injected, meaning Sentry is active.
 */
export function isEnabled() {
    return isEnabled_1();
}

/**
 * Initialise the Sentry SDK. Safe to call even when DSN is empty (no-op).
 */
export function init() {
    if (isEnabled()) {
        init_1({
            dsn: Config_dsn,
            environment: window.SENTRY_ENVIRONMENT || 'development',
            tracesSampleRate: window.SENTRY_TRACES_SAMPLE_RATE || 0.0,
            replaysSessionSampleRate: window.SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.0,
            replaysOnErrorSampleRate: window.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1.0,
            release: window.SENTRY_RELEASE || 'unknown',
            beforeSend: window.SENTRY_BEFORE_SEND || (function (event, hint) { return event; }),
        });
    }
}

/**
 * Capture an exception in Sentry.
 */
export function captureException(ex) {
    captureException_1(ex);
}

export function captureMessage(msg) {
    if (isEnabled()) {
        captureMessage_1(msg);
    }
}

export function addBreadcrumb(category, message) {
    if (isEnabled()) {
        addBreadcrumb_1({
            category: category,
            message: message,
            level: "info",
        });
    }
}

/**
 * Compute an FNV-1a 32-bit hash of an email address for privacy-safe Sentry identification.
 * The result is an 8-character lowercase hex string. Safe to call synchronously in Fable.
 * An empty or whitespace-only email produces a valid (but constant) hash; callers should
 * only pass non-empty email values (e.g., wrapped in Option.map).
 */
export function hashEmail(email) {
    const arg = fold((h, c) => (((h ^ (c.charCodeAt(0) >>> 0)) >>> 0) * 16777619), 2166136261, email.split(""));
    return toText(printf("%08x"))(arg);
}

export function setUser(monitoringId) {
    if (isEnabled()) {
        if (monitoringId == null) {
            setUser_1(defaultOf());
        }
        else {
            setUser_1({
                id: monitoringId,
            });
        }
    }
}

/**
 * Redacts sensitive payloads (passwords, verification codes) before sending breadcrumbs to Sentry.
 */
export function sanitizeMsgForBreadcrumb(msg) {
    let matchResult, userId;
    switch (msg.tag) {
        case 24: {
            switch (msg.fields[0].tag) {
                case 3: {
                    matchResult = 0;
                    break;
                }
                case 4: {
                    matchResult = 1;
                    break;
                }
                default:
                    matchResult = 4;
            }
            break;
        }
        case 27: {
            matchResult = 2;
            break;
        }
        case 29: {
            matchResult = 3;
            userId = msg.fields[0];
            break;
        }
        default:
            matchResult = 4;
    }
    switch (matchResult) {
        case 0:
            return "UpdateRegField (Password <redacted>)";
        case 1:
            return "UpdateRegField (ConfirmPassword <redacted>)";
        case 2:
            return "UpdateVerificationCode <redacted>";
        case 3:
            return toText(printf("AutoVerifyFromUrl (%s, <redacted>)"))(userId);
        default:
            return toText(printf("%A"))(msg);
    }
}

/**
 * Wrap children in a Sentry ErrorBoundary. When Sentry is disabled,
 * renders children directly (no boundary overhead).
 */
export function errorBoundary(fallback, children) {
    if (isEnabled()) {
        return createElement(ErrorBoundary, {
            fallback: fallback,
        }, ...children);
    }
    else {
        return createElement(Fragment, defaultOf_1(), ...children);
    }
}

//# sourceMappingURL=Sentry.js.map
